import { zoomToExtent } from '@/actions/mapActions';
import { highlightRoadsystemReference } from '@/actions/searchActions';
import Accordion from '@/components/objects/Accordion';
import { Vegsystem, Vegsystemreferanse, vegSysRefComparator } from '@/domain/omrader';
import { Vegobjekt } from '@/domain/vegobjekter/Vegobjekt';
import { Dispatch, useDispatch } from '@/state/store';
import { kartutsnittFromWktStrings } from '@/utils/SpatialHelper';
import React, { useState } from 'react';

type VegsystemreferanserProps = {
    vegobjekt: Vegobjekt;
};

function Vegsystemreferanser({ vegobjekt }: VegsystemreferanserProps) {
    return (
        <>
            {vegobjekt?.lokasjon?.vegsystemreferanser && [
                <dt key={'vsr-dt_' + vegobjekt.id} className="c-valgt-features__dt">
                    Vegsystemreferanser:
                </dt>,
                <dd
                    key={'vsr-dd_' + vegobjekt.id}
                    id={'valgt-features_objektid_' + vegobjekt.id}
                    className="c-valgt-features__dd"
                >
                    <VegSysRefs vegobjekt={vegobjekt} />
                </dd>,
            ]}
        </>
    );
}

function VegSysRefs({ vegobjekt }: VegsystemreferanserProps) {
    const dispatch: Dispatch = useDispatch();
    const vegSysRefs = vegobjekt.lokasjon.vegsystemreferanser.filter(
        (
            vegsystemreferanse
        ): vegsystemreferanse is Vegsystemreferanse & { vegsystem: Vegsystem } =>
            vegsystemreferanse.vegsystem != null
    );
    const [showAll, setShowAll] = useState(false);

    if (vegSysRefs.length === 0) {
        return 'Ingen vegsystemreferanser';
    } else if (vegSysRefs.length === 1) {
        return (
            <a
                key={vegSysRefs[0].toVegkartKortform()}
                className="c-valgt-features__link c-valgt-features__link--aligned"
                role="button"
                aria-description="Gå til vegsystemreferanse i kart"
                tabIndex={0}
                id={'valgtfeatureslink_' + vegobjekt.id.toString()}
                onClick={() =>
                    zoomToExtentAndShowVSRPopup(vegSysRefs[0], vegobjekt, dispatch)
                }
            >
                {transformIfKPSVeg({
                    kortform: vegSysRefs[0].toVegkartKortform(),
                    vegkategori: vegSysRefs[0].vegsystem.vegkategori,
                    kommune: vegobjekt.lokasjon.kommuner[0],
                    sideposisjon: vegSysRefs[0].metrertLokasjon?.sideposisjon,
                    kjørefelt: vegSysRefs[0].metrertLokasjon?.kjørefelt,
                })}
            </a>
        );
    }

    // Generate keys based on vegkategori, fase, nummer, strekning, and delstrekning.
    const acc: Map<string, Vegsystemreferanse[]> = new Map();

    for (const vsr of vegSysRefs) {
        const kortnavn = vsr.vegsystem.vegkategori
            .concat(vsr.vegsystem.fase ?? '')
            .concat(vsr.vegsystem.nummer + '')
            .concat(
                vsr.strekning
                    ? ' S' +
                          vsr.strekning.strekningsNummer.toString() +
                          'D' +
                          vsr.strekning.delstrekningsNummer?.toString()
                    : ''
            );

        if (acc.has(kortnavn)) {
            acc.get(kortnavn)?.push(vsr);
        } else {
            acc.set(kortnavn, [vsr]);
        }
    }

    const entries = Array.from(acc.entries());
    const visibleVsr = 10;
    const visibleEntries = showAll ? entries : entries.slice(0, visibleVsr);

    return (
        <>
            {visibleEntries.map(([key, values]) => {
                values.sort(vegSysRefComparator);

                if (values.length === 1) {
                    return (
                        <a
                            key={values[0].toVegkartKortform()}
                            className="c-valgt-features__link-list c-valgt-features__link-list--top-level"
                            onClick={() =>
                                zoomToExtentAndShowVSRPopup(
                                    values[0],
                                    vegobjekt,
                                    dispatch
                                )
                            }
                        >
                            {values[0].toVegkartKortform()}
                        </a>
                    );
                }

                return (
                    <Accordion
                        className="c-valgt-features__accordion"
                        key={key}
                        header={key}
                    >
                        <div className="c-valgt-features__accordion-group">
                            {values.map((vsr: Vegsystemreferanse, index: number) => (
                                <a
                                    key={vsr.toVegkartKortform() + '_' + index}
                                    className="c-valgt-features__link-list"
                                    onClick={() =>
                                        zoomToExtentAndShowVSRPopup(
                                            vsr,
                                            vegobjekt,
                                            dispatch
                                        )
                                    }
                                >
                                    {vsr.toVegkartKortform()}
                                </a>
                            ))}
                        </div>
                    </Accordion>
                );
            })}
            {entries.length > visibleVsr && (
                <button
                    type="button"
                    onClick={() => setShowAll(prev => !prev)}
                    className="o-expand-button"
                    style={{ marginTop: '5px' }}
                    aria-label="Utvid eller minimer visning av vegsystemreferanser"
                    title={
                        showAll
                            ? 'Minimer visning av vegsystemreferanser'
                            : `Utvid visning av vegsystemreferanser`
                    }
                >
                    {showAll ? 'Vis færre' : `Vis alle (${entries.length - visibleVsr})`}
                </button>
            )}
        </>
    );
}

export function transformIfKPSVeg({
    kortform,
    vegkategori,
    kommune,
    sideposisjon,
    kjørefelt,
}: {
    kortform: string;
    vegkategori: string;
    kommune: number;
    sideposisjon?: string | null;
    kjørefelt?: string[] | null;
}): string {
    const base = ['K', 'P', 'S'].includes(vegkategori)
        ? `${kommune} ${kortform}`
        : kortform;
    const suffixes = [sideposisjon, kjørefelt?.length ? kjørefelt.join('#') : null]
        .filter(Boolean)
        .map(value => `(${value})`)
        .join(' ');

    return suffixes ? `${base} ${suffixes}` : base;
}

function zoomToExtentAndShowVSRPopup(
    vsr: Vegsystemreferanse,
    vegobjekt: Vegobjekt,
    dispatch: Dispatch
) {
    if (!vegobjekt.geometri) {
        throw new Error(`Vegobjekt ${vegobjekt.id} mangler geometri`);
    }
    dispatch(highlightRoadsystemReference(vsr));
    dispatch(zoomToExtent(kartutsnittFromWktStrings(vegobjekt.geometri.wkt)));
}

export default Vegsystemreferanser;
